import axios from 'axios';

// Création d'une instance axios avec l'URL de base
const API = axios.create({
    baseURL: window._env_?.API_URL || process.env.REACT_APP_API_BASE_URL,
});

// Intercepteur pour ajouter le token d'authentification aux requêtes
API.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les sessions expirées
API.interceptors.response.use(
    (response) => response,
    (error) => {
        // Session expirée ou coupée (changement d'identifiants ailleurs) : seulement pour une requête
        // qui portait un jeton. Un échec de connexion (401 sur /signin) reste sur la page, avec son message.
        if (error.response?.status === 401 && error.config?.headers?.Authorization) {
            sessionStorage.removeItem('token');
            window.location.href = '/signin';
        }
        // Documents juridiques mis à jour pendant la session : on ne déconnecte pas ;
        // le rechargement remonte LegalGate, qui demande l'acceptation.
        if (error.response?.status === 403 && error.response.data?.error === 'legal_acceptance_required') {
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

// Service d'authentification
export const AuthService = {
    // Connexion
    signIn: async (credentials) => {
        try {
            const response = await API.post('/signin', credentials);
            if (response.data && response.data.token) {
                sessionStorage.setItem('token', response.data.token);
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Inscription
    signUp: async (userData) => {
        try {
            const response = await API.post('/signup', userData);
            if (response.data && response.data.token) {
                sessionStorage.setItem('token', response.data.token);
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Nouveau jeton après un changement d'e-mail ou de mot de passe : les autres sessions sont coupées.
    setToken: (token) => {
        sessionStorage.setItem('token', token);
    },

    // Déconnexion
    logout: () => {
        sessionStorage.removeItem('token');
    },

    // Vérifier si l'utilisateur est connecté
    isAuthenticated: () => {

        return !!sessionStorage.getItem('token');
    },

    getLegal: async () => (await API.get('/legal')).data,

    getLegalStatus: async () => (await API.get('/protected/legal/status')).data,

    acceptLegal: async (payload) => (await API.post('/protected/legal/accept', payload)).data,

    // Demande de réinitialisation de mot de passe
    lostPassword: async (email) => {
        try {
            const response = await API.post('/lost_password', { email });
            return response.data;
        } catch (error) {
            // Erreur réseau ou serveur : on remonte pour informer l'utilisateur
            if (!error.response || error.response.status >= 500) {
                throw error;
            }
            // 4xx (email non trouvé, etc.) : on swallow pour ne pas révéler si l'email existe
        }
    },

    // Réinitialisation du mot de passe
    resetPassword: async (token, password) => {
        try {
            const response = await API.post('/renew_password', { token, password });
            return response.data;
        } catch (error) {
            // Propager l'erreur pour la gérer dans le composant
            console.error('Erreur lors de la réinitialisation du mot de passe:', error);
            throw error;
        }
    }
};

// Service de gestion des unités
export const UnitService = {
    // Récupérer les informations de la structure et ses unités
    getStructureInfo: async () => {
        try {
            const response = await API.get('/protected/me');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Créer une nouvelle unité
    createUnit: async (unitData) => {
        try {
            // S'assurer que le type est bien inclus
            if (!unitData.type || !unitData.start_dt || !unitData.end_dt) {
                throw new Error('Le type et les dates de début et de fin sont requis');
            }

            const response = await API.post('/protected/unit', unitData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Mettre à jour une unité existante
    updateUnit: async (unitData) => {
        try {
            // S'assurer que l'ID et le type sont bien inclus
            if (!unitData.id) {
                throw new Error('L\'ID du groupe est requis');
            }
            if (!unitData.type || !unitData.start_dt || !unitData.end_dt) {
                throw new Error('Le type et les dates de début et de fin sont requis');
            }

            const response = await API.post('/protected/unit', unitData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Récupérer les devices d'une unité
    getUnitDevices: async (unitId) => {
        try {
            const response = await API.get(`/protected/units/${unitId}/devices`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Jeton à usage unique du QR de démarrage (invalide le précédent)
    createInitToken: async (unitId) => (await API.post(`/protected/unit/${unitId}/init_token`)).data,

    // Supprimer une unité
    deleteUnit: async (unitId) => {
        try {
            const response = await API.delete(`/protected/unit/${unitId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    ,

    // Mettre à jour les informations de la structure
    updateStructure: async (payload) => {
        try {
            const response = await API.put('/protected/structure', payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Supprimer un enrollment (device) d'une unité
    deleteEnrollment: async (unitId, deviceId) => {
        try {
            const response = await API.delete(`/protected/enrollment/${unitId}/${deviceId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

export const ExpectedListService = {
    getPublicKey: async (unitId) => (await API.get(`/protected/unit/${unitId}/public_key`)).data.pem,
    getMeta: async (unitId) => {
        try { return (await API.get(`/protected/unit/${unitId}/expected_list`)).data; }
        catch (e) { if (e.response?.status === 404) return null; throw e; }
    },
    send: async (unitId, { blob, key, count }) =>
        (await API.post(`/protected/unit/${unitId}/expected_list`, { blob, key, count })).data,
};

export default API;
